import { createSelector } from 'reselect';
import type { OfferType, Rate, RatedProtectionProduct } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import type { SurchargeOverrides, DealProduct, DarwinBaselineProduct, DarwinBaselineProducts } from '@makemydeal/dr-shared-types';
import { GAP_PRODUCT_CATEGORY_CODE } from '@makemydeal/dr-shared-types';
import type {
    DraftDealProduct,
    DealProductSummary,
    StateTree,
    PaymentProduct,
    RequestedSurcharges,
    DealProductWithProductCode,
    VppProductAttributes,
    DealerProduct,
    ProductPlanDetails,
    OverriddenTermFields
} from '@makemydeal/dr-dash-types';
import { RefreshContractValidationState } from '@makemydeal/dr-dash-types';

import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    calculateVehicleProtectionSummary,
    getDarwinBaselineOverrides,
    getOverriddenTermFields,
    getPlansFromProductsRates,
    getSelectedRate,
    isDealProduct,
    isProductDivergedFromBaseline,
    isVppCapProduct,
    isVppProductSelected,
    isVppUpfrontProduct,
    mapAmdToVppVehicleCondition,
    mapOfferTypeToVppDealType,
    syncVppProducts,
    toDarwinBaselineFields,
    getUniqueProviderNames
} from '../utils/vehicleProtectionUtils';
import { getCurrentOfferType, getOutsideLienholder } from './offerRedux';
import { getFirstContractApiErroredVPP, getContractApiErroredVppFieldFlags } from './contractingSelectors';
import type { ContractApiErroredVppFieldFlags } from './contractingSelectors';
import * as offerSelectors from './offerSelectors';
import * as vehicleSelectors from './vehicleSelectors';
import * as paymentSelectors from './payment';
import {
    CONTRACT_API_ERRORED_VPP_ADDRESS_FIELDS,
    CONTRACT_API_ERRORED_VPP_GAP_REQUIRED_ADDRESS_FIELDS,
    isValidNonZeroVppValue
} from '@makemydeal/dr-shared-ui-utils/validationConfigs/vehicleProtectionProductsValidationConfig';

export const getVppProducts = (state: StateTree): DraftDealProduct[] => {
    return state.vehicleProtectionV2?.products || [];
};

export const getDealProducts = (state: StateTree): DealProduct[] => {
    const products = getVppProducts(state);

    return products.filter(isDealProduct);
};

export const getSelectedDealProducts = (state: StateTree): DealProduct[] => {
    const products = getDealProducts(state);

    return products.filter((product) => product.selected);
};

export const getDarwinBaselineProducts = (state: StateTree): DarwinBaselineProducts =>
    state.vehicleProtectionV2?.darwinBaselineProducts || {};

export const getDarwinBaselineProduct = (state: StateTree, productCode: string | undefined): DarwinBaselineProduct | undefined =>
    productCode ? getDarwinBaselineProducts(state)[productCode] : undefined;

export const isProductDivergedFromDarwinBaseline = (state: StateTree, productCode: string | undefined): boolean => {
    const product = getVppProduct(state, productCode);
    if (!product) return false;

    return isProductDivergedFromBaseline(toDarwinBaselineFields(product), getDarwinBaselineProduct(state, productCode));
};

export const getVppDarwinBaselinePayload = (state: StateTree): DarwinBaselineProducts => {
    return getDarwinBaselineOverrides(getVppProducts(state), getDarwinBaselineProducts(state));
};

export const getEditableProductCode = (state: StateTree): string | undefined => state.vehicleProtectionV2?.editableProductCode;

export const getIsGapProductSelected = (state: StateTree): boolean => {
    const selectedProducts = getSelectedDealProducts(state);
    return selectedProducts.some((product) => product.productCategoryCode === 'GAP');
};

export const getIsGapRemovedForCash = (state: StateTree): boolean => {
    return state.vehicleProtectionV2?.isGapRemovedForCash ?? false;
};

export const getUnselectedDealProducts = (state: StateTree): DealProduct[] => {
    const products = getDealProducts(state);
    const dealXgProductCodes = state.vehicleProtectionV2?.dealXgProducts.map((product) => product.productCode) || [];

    return products.filter((product) => !product.selected && dealXgProductCodes.includes(product.productCode));
};

export const getSelectedManualVppProducts = createSelector([getVppProducts], (vpp) =>
    vpp.filter((product) => product.isManualProduct && product.selected)
);

export const getSelectedCatalogVppProducts = createSelector([getVppProducts], (vpp) =>
    vpp.filter((product) => !product.isManualProduct && product.selected)
);

export const getCatalogVppProducts = createSelector([getVppProducts], (vpp) => vpp.filter((product) => !product.isManualProduct));

export const getSelectedCatalogVppProductsCount = (state: StateTree): number => {
    return getSelectedCatalogVppProducts(state).length;
};

export const isFetchingVppProducts = (state: StateTree): boolean => {
    return !!state.vehicleProtectionV2?.isFetchingProducts;
};

export const isFetchingVppRates = (state: StateTree): boolean => {
    return !!state.vehicleProtectionV2?.isFetchingRates;
};

/**
 * True for any rating issue: partial failure (FETCH_RATES_SUCCESS with errors),
 * hard fetch failure, or polling timeout. Too broad to drive a retry button on
 * its own — use {@link hasVppRatingNetworkFailure} or {@link isVppRatingTimeout}.
 */
export const isVppRatingFailed = (state: StateTree): boolean => {
    return !!state.vehicleProtectionV2?.isRatingFailed;
};

/** True when rates polling exceeded MAX_FETCH_RATES_POLLING_COUNT. Implies isRatingFailed. */
export const isVppRatingTimeout = (state: StateTree): boolean => {
    return !!state.vehicleProtectionV2?.isRatingTimeout;
};

/** True only on hard rate-fetch failure (FETCH_RATES_FAILURE). The retryable signal. */
export const hasVppRatingNetworkFailure = (state: StateTree): boolean => {
    return !!state.vehicleProtectionV2?.isRatingNetworkFailure;
};

export const isVppRatingRetried = (state: StateTree): boolean => {
    return !!state.vehicleProtectionV2?.isFetchRatesRetried;
};

export const hasSelectedProductsWithInapplicableRateAttributes = (state: StateTree): boolean => {
    const products = getVppSelectedProducts(state);

    return products.some((product) => {
        // Products with intentional deal-level term overrides will not match any rate card entry, so they would
        // otherwise be flagged here. Exempt them to stay consistent with the per-row alert and edit/select exemptions
        // in getVppProductAttributes.
        if (isProductTermFieldOverridden(state, product.productCode)) {
            return false;
        }

        const ratedProduct = getVppRatedProduct(state, product.productCode);
        const hasSelectedRate = getSelectedRate(ratedProduct, product) !== undefined;

        return ratedProduct && !hasSelectedRate;
    });
};

export const getVppRateToken = (state: StateTree): string => {
    return state.vehicleProtectionV2?.rateToken ?? '';
};

export const getVppSelectedProducts = (state: StateTree): DraftDealProduct[] => {
    return getVppProducts(state).filter(isVppProductSelected);
};

export const getVppSelectedProductCodes = (state: StateTree): string[] => {
    return getVppSelectedProducts(state)
        .map((product) => product.productCode)
        .filter((productCode): productCode is string => typeof productCode === 'string');
};

export const getVppProductsEligibleForSelection = (state: StateTree): DraftDealProduct[] => {
    const products = getCatalogVppProducts(state);
    const productsAttributes = getVppProductAttributes(state);

    return products.filter(({ productCode, selected }) => {
        if (!productCode) {
            return false;
        }

        const productAttributes = productsAttributes[productCode];

        if (selected) {
            return !productAttributes?.unselectDisabled;
        } else {
            return !productAttributes?.selectDisabled;
        }
    });
};
export const getVppProduct = (state: StateTree, productCode: string | undefined): DraftDealProduct | undefined => {
    const product = state.vehicleProtectionV2?.products?.find((product) => product.productCode === productCode);

    return product;
};

export const getVppInitialDealXgProducts = (state: StateTree): DraftDealProduct[] => {
    return state.vehicleProtectionV2?.dealXgProducts || [];
};

export const getVppInitialDealXgProduct = (state: StateTree, productCode: string | undefined): DraftDealProduct | undefined => {
    const product = state.vehicleProtectionV2?.dealXgProducts.find((product) => product.productCode === productCode);

    return product;
};

export const getVppDealerProduct = (state: StateTree, productCode: string | undefined): Partial<DealerProduct> | undefined => {
    const product = state.vehicleProtectionV2?.dealerProducts.find((product) => product.productCode === productCode);

    return product;
};

export const getVppDealerProducts = (state: StateTree): Partial<DealerProduct>[] => {
    const products = state.vehicleProtectionV2?.dealerProducts || [];

    return products;
};

export const getVppSurchargesOverrides = (state: StateTree): SurchargeOverrides => {
    const offerDetails = offerSelectors.getOfferDetailsForMenu(state);
    const vehicleDetails = vehicleSelectors.getVehicle(state);
    const surcharges = state.vehicleProtectionV2?.surcharges;

    return {
        ...surcharges,
        vin: vehicleDetails.vin,
        dealType: mapOfferTypeToVppDealType(offerDetails.offerType as OfferType),
        inventoryType: mapAmdToVppVehicleCondition(vehicleDetails.condition),
        usage: vehicleSelectors.getVehicleUse(state),
        annualMiles: offerDetails.annualMiles,
        amountFinanced: offerDetails.totalFinanced,
        apr: offerDetails.apr,
        monthlyPayment: offerDetails.monthlyPayment,
        sellingPrice: offerDetails.offerPrice,
        term: offerDetails.term,
        odometer: vehicleDetails.mileage,
        retailPrice: vehicleDetails.retailPrice,
        color: vehicleDetails.exteriorColor,
        year: vehicleDetails.year,
        make: vehicleDetails.make,
        model: vehicleDetails.model,
        chromeStyleId: vehicleDetails.chromeStyleId,
        stockNumber: vehicleDetails.stockNumber,
        trim: vehicleDetails.trim,
        lienHolderName: getOutsideLienholder(state)?.lienHolder,
        inServiceDate: surcharges?.inServiceDate ?? vehicleDetails.inServiceDate,
        // Toggle is read post-bootstrap only: this selector also runs on the offer-save path,
        // where reading toggles early trips the precondition check.
        ...(featureToggleSelectors.areFeatureTogglesAvailable(state) &&
            featureToggleSelectors.isUsageAndCustomerTypeSurchargesEnabled(state) && {
                isBusinessCustomer: offerSelectors.getIsBusinessShopper(state)
            })
    };
};

export const getVppRequestedSurcharges = (state: StateTree): RequestedSurcharges => {
    return (
        state.vehicleProtectionV2?.requestedSurcharges || {
            usedSurchargesAttributes: [],
            requiredSurchargesAttributes: []
        }
    );
};

export const isPaymentFailed = (state: StateTree): boolean => {
    return state.vehicleProtectionV2?.isPaymentFailed || false;
};

export const getVppSummary = (state: StateTree): DealProductSummary => {
    const catalogProducts = getCatalogVppProducts(state);
    const manualProducts = getVppProducts(state).filter((p) => p.isManualProduct);
    const products = [...catalogProducts, ...manualProducts];

    const term = offerSelectors.getSelectedTermMonths(state);
    const calculatedSummary = calculateVehicleProtectionSummary({ products, term });
    const areFeatureTogglesAvailable = featureToggleSelectors.areFeatureTogglesAvailable(state);

    // NOTE: if toggle is turned on - use PO monthly payment of each individual product whatever it's possible
    if (areFeatureTogglesAvailable && featureToggleSelectors.usePaymentProductsMonthlyPayment(state)) {
        const isCalculatingPayment = paymentSelectors.isCalculatingPayment(state);

        if (!isPaymentFailed(state) && !isCalculatingPayment) {
            const selectedProducts = [...getSelectedCatalogVppProducts(state), ...getSelectedManualVppProducts(state)];

            calculatedSummary.productMonthlyPayment = selectedProducts.reduce((totalMonthlyPayment, product) => {
                const defaultMonthlyPayment = product.productPrice && term ? product.productPrice / term : 0;
                const productMonthlyPayment = product.productMonthlyPayment ?? defaultMonthlyPayment;

                return totalMonthlyPayment + productMonthlyPayment;
            }, 0);
        }
    }

    return calculatedSummary;
};

export const getVppRatedProduct = (state: StateTree, productCode: string | undefined): RatedProtectionProduct | undefined => {
    const ratedProduct = state.vehicleProtectionV2?.rates?.find((rate) => rate.productCode === productCode);

    return ratedProduct;
};

export const getRatedProductsByCategoryCodeAndProvider = (
    state: StateTree,
    productCategoryCode: string,
    providerCode: string
): RatedProtectionProduct[] =>
    state.vehicleProtectionV2?.rates?.filter((rate) => {
        return rate.productCategoryCode === productCategoryCode && rate.providerCode === providerCode;
    }) || [];

export const getRatedProductsWithSameCategoryAndProvider = (
    state: StateTree,
    productCode: string | undefined
): RatedProtectionProduct[] => {
    const product = getVppRatedProduct(state, productCode);
    if (!product) {
        return [];
    }

    const { productCategoryCode, providerCode } = product;
    const matchingRatedProducts = getRatedProductsByCategoryCodeAndProvider(state, productCategoryCode, providerCode);

    return matchingRatedProducts;
};

export const getPlansForProductsWithSameCategoryAndProvider = (
    state: StateTree,
    productCode: string | undefined
): ProductPlanDetails[] => {
    const ratedProducts = getRatedProductsWithSameCategoryAndProvider(state, productCode);

    return ratedProducts.reduce((plansForAllProducts, ratedProduct) => {
        const productPlans = getPlansFromProductsRates(ratedProduct.rates).map((plan) => ({
            productProviderPlan: plan,
            productCode: ratedProduct.productCode,
            productName: ratedProduct.productName
        }));

        return plansForAllProducts.concat(productPlans);
    }, [] as ProductPlanDetails[]);
};

export const getVppProductRates = (state: StateTree, productCode: string | undefined): Rate[] | undefined => {
    const ratedProduct = getVppRatedProduct(state, productCode);

    return ratedProduct?.rates;
};

export const getVppProductRateAttributes = (
    state: StateTree,
    productCode: string | undefined
): RatedProtectionProduct['rateAttributes'] => {
    const ratedProduct = getVppRatedProduct(state, productCode);

    return ratedProduct?.rateAttributes || [];
};

export const isNonRateableProduct = (state: StateTree, productCode: string | undefined): boolean => {
    const ratedProduct = getVppRatedProduct(state, productCode);

    return !ratedProduct || ratedProduct.rateAttributes.length === 0;
};

export const getProductOverriddenTermFields = (state: StateTree, productCode: string | undefined): OverriddenTermFields => {
    const product = getVppProduct(state, productCode);

    if (!product) {
        return {};
    }

    const rateAttributes = getVppProductRateAttributes(state, productCode);
    const rates = getVppProductRates(state, productCode) || [];

    return getOverriddenTermFields(product, rateAttributes, rates);
};

export const isProductTermFieldOverridden = (state: StateTree, productCode: string | undefined): boolean => {
    return Object.keys(getProductOverriddenTermFields(state, productCode)).length > 0;
};

export const isProductProviderNameOverridden = (state: StateTree, productCode: string | undefined): boolean => {
    const product = getVppProduct(state, productCode);
    const providerName = product?.productProviderName;

    if (!product?.productCategoryCode || !providerName) {
        return false;
    }

    const originalProviders = getVppCategoryOriginalProviders(state, product.productCategoryCode.toUpperCase());

    if (!originalProviders.length) {
        return false;
    }

    return !originalProviders.includes(providerName);
};

export const hasMissingRequiredAttributes = (state: StateTree): boolean => {
    const dealerProducts = getVppDealerProducts(state);
    let hasMissingAttributes = false;
    for (const product of dealerProducts) {
        if (product.rateRequirements?.hasMissingField) {
            hasMissingAttributes = true;
            break;
        }
    }
    return hasMissingAttributes;
};

export const getFailedPaymentProductCodes = (state: StateTree): string[] | undefined => {
    const lastSuccessfulPaymentProductCodes = state.vehicleProtectionV2?.lastSuccessfulPaymentProductCodes ?? [];
    return state.vehicleProtectionV2?.paymentProductCodes.filter(
        (productCode) => !lastSuccessfulPaymentProductCodes.includes(productCode)
    );
};

export const getTargetProducts = (state: StateTree): DealProduct[] => state.vehicleProtectionV2?.targetProducts || [];

export const getIsTemplateProductsApplied = (state: StateTree): boolean =>
    state.vehicleProtectionV2?.isTemplateProductsApplied || false;

export const isApplyingTemplateProducts = (state: StateTree): boolean =>
    getTargetProducts(state).length > 0 && !getIsTemplateProductsApplied(state);

export const getLastSuccessfulPaymentProducts = (state: StateTree): DraftDealProduct[] => {
    const lastSuccessfulPaymentProductCodes = state.vehicleProtectionV2?.lastSuccessfulPaymentProductCodes ?? [];

    return getVppProducts(state).filter(
        (product) => product.productCode && lastSuccessfulPaymentProductCodes.includes(product.productCode)
    );
};

export const getVppProductAttributes = (state: StateTree): Record<string, VppProductAttributes> => {
    const products = getVppProducts(state) as DealProductWithProductCode[];
    const isFetchingRates = isFetchingVppRates(state);
    const isFetchingProducts = isFetchingVppProducts(state);
    const ratingFailed = isVppRatingFailed(state);
    const paymentFailed = isPaymentFailed(state);
    const failedProductCodes = getFailedPaymentProductCodes(state);
    return products.reduce((attributesMap, product) => {
        const productAttributes: VppProductAttributes = {};

        // Products with intentional deal-level term overrides will not match any rate card entry.
        // They must remain editable and selectable, so they are exempt from rate-match-based disabling.
        // NOTE: only term-field overrides are checked here. Provider-name overrides do not participate in
        // rate matching (see doesRateMatchProduct), so they never trigger the disabling, and checking them
        // would introduce a circular selector dependency via getVppEditableProducts -> getVppProductAttributes.
        const isProductOverridden = isProductTermFieldOverridden(state, product.productCode);

        const initialDealXgProduct = getVppInitialDealXgProduct(state, product.productCode);
        const dealerProduct = getVppDealerProduct(state, product.productCode);
        const productRates = getVppProductRates(state, product.productCode);
        const rateAttributes = getVppProductRateAttributes(state, product.productCode);

        const selectedRate = getSelectedRate({ rates: productRates, rateAttributes }, product);
        const productCausedPaymentError = !!failedProductCodes?.includes(product.productCode);
        const hasMissingAttributes = dealerProduct && dealerProduct.rateRequirements?.hasMissingField;

        const hasInapplicableRateAttributes =
            !isProductOverridden && initialDealXgProduct && product.selected && productRates && !selectedRate;
        const hasInapplicablePlan =
            hasInapplicableRateAttributes &&
            rateAttributes.includes('productProviderPlan') &&
            productRates.every(({ productProviderPlan }) => productProviderPlan !== product.productProviderPlan);

        const productCostChanged =
            initialDealXgProduct &&
            selectedRate &&
            product.selected &&
            Number.isFinite(product.productCost) &&
            Number.isFinite(selectedRate.productCost) &&
            product.productCost !== selectedRate.productCost;

        if (isFetchingProducts || isFetchingRates) {
            productAttributes.editingDisabled = true;
            productAttributes.selectDisabled = true;
            productAttributes.unselectDisabled = true;
        }

        if (initialDealXgProduct && !dealerProduct) {
            productAttributes.editingDisabled = true;
            if (!isFetchingProducts) {
                productAttributes.productDisappeared = true;
            }
        } else if (hasMissingAttributes) {
            productAttributes.missingRequiredAttributes = true;
            productAttributes.editingDisabled = true;
            productAttributes.selectDisabled = true;
            productAttributes.unselectDisabled = true;
        } else if (!productRates || productRates.length === 0) {
            productAttributes.editingDisabled = true;
            productAttributes.selectDisabled = true;
            if (!isFetchingRates) {
                productAttributes.productWithoutRates = true;
            }
        } else if (!initialDealXgProduct && dealerProduct) {
            productAttributes.newProductFromFie = true;
        } else if (productCostChanged) {
            productAttributes.selectedProductCostChanged = true;
        }
        if (ratingFailed && !selectedRate && !isProductOverridden) {
            productAttributes.editingDisabled = true;
            productAttributes.selectDisabled = true;
        }
        if (paymentFailed && productCausedPaymentError) {
            productAttributes.paymentFailed = true;
        }
        if (hasInapplicableRateAttributes) {
            productAttributes.hasInapplicableRateAttribute = true;
        }
        if (hasInapplicablePlan) {
            productAttributes.hasInapplicablePlan = true;
        }
        if (
            featureToggleSelectors.isMVVppOverrideIndicatorEnabled(state) &&
            isProductDivergedFromDarwinBaseline(state, product.productCode)
        ) {
            productAttributes.isDivergedFromDarwinBaseline = true;
        }

        attributesMap[product.productCode] = productAttributes;
        return attributesMap;
    }, {} as Record<string, VppProductAttributes>);
};

// Mirrors the row-highlight condition in VehicleProtectionTableCompact (selected catalog product
// diverged from its Darwin baseline) so the "Manually Edited" legend also covers edited-catalog-product
// rows, not just wholly manual ones.
export const getSelectedDivergedFromDarwinBaselineVppProducts = (state: StateTree): DraftDealProduct[] => {
    const productAttributes = getVppProductAttributes(state);

    return getSelectedCatalogVppProducts(state).filter(
        (product) => product.productCode && productAttributes[product.productCode]?.isDivergedFromDarwinBaseline
    );
};

export const getVppPaymentProducts = (state: StateTree): PaymentProduct[] => {
    const isLeaseDeal = getCurrentOfferType(state) === LEASE;

    const products = [...getSelectedCatalogVppProducts(state), ...getSelectedManualVppProducts(state)];

    return products
        .filter((product) => Boolean(product.productCategoryCode || product.isManualProduct))
        .map((product) => ({
            categoryCode: product.productCategoryCode!,
            id: product.productCode,
            price: product.productPrice ?? 0,
            ...(Number.isFinite(product.productCost) && { cost: product.productCost }),
            ...(product.profitLocation && { backEndIndicator: product.profitLocation === 'Back' }),
            ...(isLeaseDeal && {
                residualType: product.productResidualType,
                residualValue: product.productResidualValue,
                priceCapitalizedIndicator: product.isProductPriceCapitalized ?? true
            })
        }));
};

export const getVppEstimatedPriceTotals = (state: StateTree): { backEnd: number; frontEnd: number } =>
    getVppPaymentProducts(state).reduce(
        (totals, product) =>
            // A product without a profitLocation falls back to back-end, as it does in pricing calculations.
            product.backEndIndicator === false
                ? { ...totals, frontEnd: totals.frontEnd + product.price }
                : { ...totals, backEnd: totals.backEnd + product.price },
        { backEnd: 0, frontEnd: 0 }
    );

export const getVppEditableProducts = (state: StateTree) => {
    return getCatalogVppProducts(state).filter((product) => {
        const productAttributes = getVppProductAttributes(state);
        const isEditable = product.productCode && !productAttributes[product.productCode]?.editingDisabled;

        return isEditable;
    });
};

export const getVppCategoryProviders = (state: StateTree, productCategoryCode: string): string[] => {
    const products = getVppEditableProducts(state);
    const categoryProducts = products.filter((product) => product.productCategoryCode === productCategoryCode);

    return getUniqueProviderNames(categoryProducts.map((product) => product.productProviderName));
};

export const getCapVppTotal = (state: StateTree): number => {
    return getSelectedCapVppProducts(state).reduce((total, product) => {
        const productPrice = product.productPrice ?? 0;

        return total + productPrice;
    }, 0);
};
export const getSelectedUpfrontVppProducts = (state: StateTree): DraftDealProduct[] => {
    return getSelectedCatalogVppProducts(state).filter(isVppUpfrontProduct);
};

export const getUpfrontVppTotal = (state: StateTree): number => {
    return getSelectedUpfrontVppProducts(state).reduce((total, product) => {
        const productPrice = product.productPrice ?? 0;

        return total + productPrice;
    }, 0);
};

export const getSelectedCapVppProducts = (state: StateTree): DraftDealProduct[] => {
    return getSelectedCatalogVppProducts(state).filter(isVppCapProduct);
};

/**
 * Returns Manager View (dr-dash) specific  total price of selected VPP
 */
export const getTotalMvVppSelectionPrice = (state: StateTree): number => {
    const products = getCatalogVppProducts(state);

    return products.reduce((totalVppSelectionPrice, product) => {
        if (product.selected) {
            totalVppSelectionPrice += product.productPrice || 0;
        }

        return totalVppSelectionPrice;
    }, 0);
};

const isGapProductSupportedForCurrentDealType = (
    state: StateTree,
    dealerProducts: Partial<DealerProduct>[],
    productCode?: string
): boolean => {
    if (!productCode) return false;

    const currentDealType = mapOfferTypeToVppDealType(getCurrentOfferType(state) as OfferType);
    const gapDealerProduct = dealerProducts.find((p) => p.productCode === productCode);
    const supportedDealTypes = gapDealerProduct?.dealerSettings?.dealTypes;

    return Boolean(supportedDealTypes) && (supportedDealTypes as string[]).includes(currentDealType);
};

export type ContractGapProduct = {
    productCode?: string;
    isManualProduct?: boolean;
    streetAddress?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    terms?: number;
};

/**
 * Every selected, deal-type-supported GAP product for contract validation, each carrying its identity so
 * errors can be attributed to one row. Included when it has a provider address or a coverage length.
 */
export const getSelectedGapProductsForContract = (state: StateTree): ContractGapProduct[] => {
    const vehicleProtectionState = state.vehicleProtectionV2;
    if (!vehicleProtectionState) return [];

    const dealerProducts = vehicleProtectionState.dealerProducts ?? [];
    const validDealerProducts = dealerProducts.filter((p): p is DealerProduct => p.productName !== undefined);
    const mergedProducts = syncVppProducts(vehicleProtectionState, validDealerProducts);

    return mergedProducts
        .filter(
            (product) =>
                product.productCategoryCode === GAP_PRODUCT_CATEGORY_CODE &&
                product.selected === true &&
                isGapProductSupportedForCurrentDealType(state, dealerProducts, product.productCode) &&
                (Boolean(product.productProviderAddress) || product.productCoverageLength !== undefined)
        )
        .map((gapProduct) => ({
            productCode: gapProduct.productCode,
            isManualProduct: gapProduct.isManualProduct,
            streetAddress: gapProduct.productProviderAddress?.addressLine1,
            city: gapProduct.productProviderAddress?.city,
            state: gapProduct.productProviderAddress?.stateProvince,
            zip: gapProduct.productProviderAddress?.postalCode,
            terms: gapProduct.productCoverageLength
        }));
};

export type ContractApiErroredVPP = DealProduct & {
    apiMiles?: number;
    apiTerms?: number;
    apiProviderName?: string;
    apiCity?: string;
    apiZip?: string;
    apiState?: string;
    apiStreetAddress?: string;
    hasApiMilesError?: boolean;
    hasApiTermsError?: boolean;
    hasApiProviderNameError?: boolean;
    hasApiCityError?: boolean;
    hasApiZipError?: boolean;
    hasApiStateError?: boolean;
    hasApiStreetAddressError?: boolean;
};

/** Passed for the numeric field the API did not flag, so it never trips the `isValidNonZeroVppValue` check downstream. */
const API_ERROR_FIELD_VALID_SENTINEL = 1;
/** Passed for the string field the API did not flag, so it never trips the REQUIRED check downstream. */
const API_ERROR_STRING_FIELD_VALID_SENTINEL = 'valid';

const isGapProduct = (product: DealProduct): boolean => product.productCategoryCode === GAP_PRODUCT_CATEGORY_CODE;

export const isContractApiErroredVppProduct = (product: DealProduct, fieldFlags: ContractApiErroredVppFieldFlags): boolean => {
    if ((product as DraftDealProduct).isManualProduct) return false;
    const hasAddressError = CONTRACT_API_ERRORED_VPP_ADDRESS_FIELDS.some(({ flag }) => fieldFlags[flag]);
    const isNonCoverageOnlyError =
        !fieldFlags.hasMilesError && !fieldFlags.hasTermsError && (hasAddressError || fieldFlags.hasProviderNameError);
    const address = product.productProviderAddress;
    return (
        (!isNonCoverageOnlyError &&
            (!isValidNonZeroVppValue(product.productCoverageMiles) || !isValidNonZeroVppValue(product.productCoverageLength))) ||
        (fieldFlags.hasProviderNameError && !product.productProviderName?.trim()) ||
        (isGapProduct(product) &&
            hasAddressError &&
            CONTRACT_API_ERRORED_VPP_ADDRESS_FIELDS.some(({ sourceKey, flag }) => fieldFlags[flag] && !address?.[sourceKey]))
    );
};

export const withoutGapContractRequiredApiErrors = (product: ContractApiErroredVPP): ContractApiErroredVPP =>
    CONTRACT_API_ERRORED_VPP_GAP_REQUIRED_ADDRESS_FIELDS.reduce(
        (acc, { apiKey }) => ({ ...acc, [apiKey]: API_ERROR_STRING_FIELD_VALID_SENTINEL }),
        product
    );

export const getContractApiErroredVPP = createSelector(
    [
        (state: StateTree) => getSelectedDealProducts(state),
        (state: StateTree) => getFirstContractApiErroredVPP(state),
        (state: StateTree) => getContractApiErroredVppFieldFlags(state)
    ],
    (products, firstErroredProduct, fieldFlags): ContractApiErroredVPP | undefined => {
        const { hasMilesError, hasTermsError } = fieldFlags;
        const invalidProduct = products.find(
            (p) => p.productCode === firstErroredProduct?.productCode && isContractApiErroredVppProduct(p, fieldFlags)
        );

        if (!invalidProduct) return undefined;

        const isGap = isGapProduct(invalidProduct);
        return CONTRACT_API_ERRORED_VPP_ADDRESS_FIELDS.reduce<ContractApiErroredVPP>(
            (acc, { flag, sourceKey, apiKey, hasApiErrorKey }) => {
                const hasError = isGap && fieldFlags[flag];
                return {
                    ...acc,
                    [apiKey]: hasError
                        ? invalidProduct.productProviderAddress?.[sourceKey] ?? ''
                        : API_ERROR_STRING_FIELD_VALID_SENTINEL,
                    [hasApiErrorKey]: hasError
                };
            },
            {
                ...invalidProduct,
                apiMiles: hasMilesError ? invalidProduct.productCoverageMiles ?? 0 : API_ERROR_FIELD_VALID_SENTINEL,
                apiTerms: hasTermsError ? invalidProduct.productCoverageLength ?? 0 : API_ERROR_FIELD_VALID_SENTINEL,
                apiProviderName: fieldFlags.hasProviderNameError
                    ? invalidProduct.productProviderName ?? ''
                    : API_ERROR_STRING_FIELD_VALID_SENTINEL,
                hasApiMilesError: hasMilesError,
                hasApiTermsError: hasTermsError,
                hasApiProviderNameError: fieldFlags.hasProviderNameError
            }
        );
    }
);

export const getContractApiErroredVPPs = createSelector([getContractApiErroredVPP], (product): ContractApiErroredVPP[] => {
    return product ? [product] : [];
});

export const isVPPTemplateProductsLoading = (state: StateTree): boolean => {
    const isVppProductsFetching = isFetchingVppProducts(state);
    const isVppRatesFetching = isFetchingVppRates(state);
    return (isVppProductsFetching || isVppRatesFetching) && isApplyingTemplateProducts(state);
};

export const getIsGapCappedByLender = (state: StateTree): boolean => {
    return state.vehicleProtectionV2?.isGapCappedByLender || false;
};

export const getRefreshContractValidationState = (state: StateTree): RefreshContractValidationState => {
    return state.vehicleProtectionV2?.refreshContractValidationState ?? RefreshContractValidationState.IDLE;
};

export const getVppCategoryOriginalProviders = (state: StateTree, productCategoryCode: string): string[] => {
    const dealerProducts = state.vehicleProtectionV2?.dealerProducts || [];
    const editableProducts = getVppEditableProducts(state);

    const categoryProductCodes = editableProducts
        .filter((product) => product.productCategoryCode === productCategoryCode)
        .map((product) => product.productCode);

    const matchingDealerProducts = dealerProducts.filter((dp) => dp.productCode && categoryProductCodes.includes(dp.productCode));

    return getUniqueProviderNames(matchingDealerProducts.map((dp) => dp.dealerSettings?.providerName));
};

export const getVppOriginalProviderName = (state: StateTree, productCode: string | undefined): string | undefined => {
    if (!productCode) return undefined;
    const dealerProduct = state.vehicleProtectionV2?.dealerProducts?.find((dp) => dp.productCode === productCode);
    return dealerProduct?.dealerSettings?.providerName;
};

/**
 * Returns dropdown options for the provider select paired as `{ value, label }`, where `value` is the
 * dealer's original (non-overridden) provider name and `label` is the current in-state provider name
 * (which may be an override). This lets the dropdown display overridden provider names from sibling
 * products while keeping a stable value the parent can use to switch between products in the category.
 */
export const getVppCategoryProviderOptions = (
    state: StateTree,
    productCategoryCode: string
): { value: string; label: string }[] => {
    const dealerProducts = state.vehicleProtectionV2?.dealerProducts || [];
    const editableProducts = getVppEditableProducts(state);
    const categoryProducts = editableProducts.filter((product) => product.productCategoryCode === productCategoryCode);

    const options: { value: string; label: string }[] = [];
    const seenValues = new Set<string>();

    for (const product of categoryProducts) {
        const dealerProduct = dealerProducts.find((dp) => dp.productCode === product.productCode);
        const value = dealerProduct?.dealerSettings?.providerName;
        if (!value || seenValues.has(value)) continue;
        seenValues.add(value);
        options.push({ value, label: product.productProviderName || value });
    }

    return options;
};
